import type { ComponentProps, ForwardedRef } from "react";
import { forwardRef, useEffect, useRef } from "react";
import { Autocomplete } from "ancestry-ui-react";
import { useTranslation } from "react-i18next";
import type { SelectedPlace } from "@/entities/place";
import { useFieldRefs, useFormAPI, useValidation } from "@/shared/forms";
import { useEffectEvent } from "@/shared/hooks/useEffectEvent";
import { useFieldContext } from "../FieldContext";
import type { PlaceAutocompleteProps } from "./types";

const PLACE_AUTOCOMPLETE_SOURCE = "https://placepfx.ancestry.com/s/?&maxCount=10&cultureId=en_US";

export const PlaceAutocompleteField = forwardRef<HTMLInputElement, PlaceAutocompleteProps>(
    function PlaceAutocompleteField(
        { ariaLabel, id, placeholder, value, onChange, required, ...rest },
        _ref: ForwardedRef<HTMLInputElement>,
    ): JSX.Element {
        const { t } = useTranslation();
        useValidation(id, {
            errorLocation: "callout",
            rules: { required },
        });

        const { fieldPath } = useFieldContext();
        const { getFieldValue } = useFormAPI();
        const { registerFieldRef, unregisterFieldRef } = useFieldRefs();
        const resultsRef = useRef<{ HName: string; Id: number | string }[]>([]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
            onChange({ ...value, location: e.target.value, gpid: null });
        };

        const handleItemSelect = useEffectEvent(({ item }: { item: SelectedPlace }): void => {
            onChange({ ...value, location: item.HName, gpid: item.Id });
        });

        const handleResponse: ComponentProps<typeof Autocomplete>["onResponse"] = useEffectEvent(({ results }) => {
            resultsRef.current = results;

            const match = results.find(
                (result) =>
                    result.HName.replace(/\s+/g, "").toLowerCase() ===
                    value?.location.replace(/\s+/g, "").toLowerCase(),
            );

            if (match) onChange({ ...value, gpid: match.Id });
        });

        const handleClose = useEffectEvent((e: { element: HTMLInputElement }) => {
            const inputValue = e.element.value;
            // We may get stale values in "field" reference, so we retrieve it programatically
            const currentValue: PlaceAutocompleteProps["value"] = getFieldValue(fieldPath);
            // Handle race condition, as handleClose may be triggered right after handleItemSelect
            if (!inputValue || inputValue === currentValue?.location) return;

            const match = resultsRef.current.find(
                (result) =>
                    result.HName.replace(/\s+/g, "").toLowerCase() === inputValue.replace(/\s+/g, "").toLowerCase(),
            );

            if (match) {
                onChange({ ...value, location: match.HName, gpid: match.Id });
            }
        });

        useEffect(() => unregisterFieldRef(`${fieldPath}.location`), [fieldPath, unregisterFieldRef]);

        return (
            <Autocomplete
                id={id}
                ref={(autocompleteRef: { content: React.RefObject<HTMLInputElement> }) => {
                    if (autocompleteRef?.content?.current) {
                        registerFieldRef(
                            `${fieldPath}.location`,
                            autocompleteRef.content?.current as unknown as React.RefObject<HTMLElement>,
                        );
                    }
                }}
                attributes={{
                    "aria-label": ariaLabel,
                    placeholder: placeholder ?? t("event.enterLocationPlaceholder"),
                    value: value?.location ?? "",
                    onChange: handleChange,
                    ...rest,
                }}
                dataType="jsonp"
                source={PLACE_AUTOCOMPLETE_SOURCE}
                searchKey="HName"
                queryParameter="prefix"
                onItemSelect={handleItemSelect}
                onResponse={handleResponse}
                onClose={handleClose}
            />
        );
    },
);
